// Транспорт раздела «Каршеринг»: парк, поездки, заправки. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ ХУК В api/p73x.ts
// api/p73x.ts обслуживает раздел «Овертаймы и командировки» — заработок людей поимённо,
// закрытый на одну учётку. Каршеринг из того же зеркала, но разряд данных другой:
// служебная машина это имущество компании, и владелец может решить открыть её шире.
// Пока это два разных решения о доступе, транспорт держим врозь — иначе однажды хук
// «покажи поездки» переиспользуют внутри закрытого раздела и наоборот.
//
// Записи здесь нет и появиться не должно: источник правды — портал 73, инженеры работают
// в нём. Раздел в CRM — обзор.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/p73x'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/p73x.ts. Продублирован намеренно:
 * общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк значило бы
 * поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 403 приходит от BFF, а не от ядра: раздел выдаётся не по роли, а по учётке.
    // Отдельное сообщение нужно потому, что «HTTP 403» человек читает как поломку.
    if (res.status === 403) {
      throw new Error('раздел «Каршеринг» вам пока не открыт: он выдаётся не по роли, '
        + 'а по учётке, и решение об этом принимает владелец')
    }
    // 400 от ядра здесь — это «не назван человек и не запрошен свод по всем».
    // Пустая таблица читалась бы как «заправок не было», а это ложь про деньги людей.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. p73x_api.py, раздел «каршеринг») ───────────────

export interface Мостик {
  d1_id: number | null
  bitrix_id: number | null
}

export interface СудноКарш extends Мостик {
  name: string | null
  imo: string | null
}

export interface ЗаявкаКарш extends Мостик {
  title: string | null
  status: string | null
  location: string | null
}

export interface ИнженерКарш extends Мостик {
  name: string | null
}

interface Мостики {
  engineer: ИнженерКарш | null
  deal: ЗаявкаКарш | null
  vessel: СудноКарш | null
}

export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  restricted?: string
  restricted_why?: string
}

/**
 * Машина парка.
 *
 * `plate` у личной машины ВСЕГДА null, `title` — подпись без номера и без марки: ядро
 * закрыло это маской в SELECT (p73x_api.ГОСНОМЕР и p73x_api.МАШИНА). Экран на маску не
 * полагается вслепую — он показывает `kind`, чтобы человек видел, почему номера нет.
 */
export interface МашинаСтрока {
  d1_id: number
  kind: string | null
  title: string | null
  plate: string | null
  owner_name: string | null
  region: string | null
  status: string | null
  active: boolean | null
  last_odometer: number | null
  fuel_card_in_car: boolean | null
  no_fuel_card: boolean | null
  osago_until: string | null
  insurance_until: string | null
  inspection_until: string | null
  service_interval_km: number | null
  last_service_odo: number | null
  trips_window: number | null
  km_window: number | null
  trips_open: number | null
  last_trip_at: string | null
}

export interface ПаркИтоги {
  n: number | null
  service_n: number | null
  personal_n: number | null
  active_n: number | null
  busy_n: number | null
  no_card_n: number | null
  fuel_by_km_n: number | null
  osago_known: number | null
  inspection_known: number | null
  service_interval_known: number | null
}

export interface ПоездкаСтрока extends Мостики {
  d1_id: number
  driver_name: string | null
  region: string | null
  purpose: string | null
  /** null, если поездка личная: куда человек съездил по своим делам, мы не показываем. */
  destination: string | null
  /** true ровно тогда, когда текст был, но закрыт. «Пусто» и «закрыто» — разные вещи. */
  destination_hidden: boolean | null
  date_out: string | null
  date_back: string | null
  odo_start: number | null
  odo_end: number | null
  km: number | null
  fuel_liters: number | null
  fuel_state: string | null
  fuel_cash: boolean | null
  fuel_cash_amount: number | null
  take_issues: string | null
  return_issues: string | null
  status: string | null
  flagged: boolean | null
  source: string | null
  car_d1_id: number | null
  car_title: string | null
  car_plate: string | null
  car_kind: string | null
  deal_raw: string | null
}

export interface ПоездкиИтоги {
  n: number | null
  km: number | null
  km_skipped: number | null
  km_missing: number | null
  open_n: number | null
  flagged_n: number | null
  personal_n: number | null
  private_n: number | null
  people: number | null
}

export interface ЗаправкаСтрока extends Мостики {
  d1_id: number
  driver_name: string | null
  region: string | null
  refuel_date: string | null
  odometer: number | null
  liters: number | null
  amount_rub: number | null
  paid_km: number | null
  actual_km: number | null
  balance_km: number | null
  comment: string | null
  source: string | null
  car_d1_id: number | null
  car_title: string | null
  car_kind: string | null
}

export interface ЗаправкиИтоги {
  n: number | null
  liters: number | null
  amount_rub: number | null
  /** Сколько строк из выборки вообще несут сумму: рубли заполнены далеко не всегда. */
  amount_rows: number | null
  paid_km: number | null
  balance_rows: number | null
  odometer_rows: number | null
  people: number | null
  cars: number | null
  first_day: string | null
  last_day: string | null
}

export interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

export interface СправочникиКарш {
  car_kinds: Record<string, number>
  car_regions: Record<string, number>
  car_status: Record<string, number>
  trip_purpose: Record<string, number>
  trip_source: Record<string, number>
  trip_regions: Record<string, number>
  refuel_months: Record<string, number>
  counts: Record<string, number | string | null>
  freshness: Свежесть[]
  data_notes: Record<string, string>
}

// ─────────────────────────────── хуки ───────────────────────────────

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

/**
 * Справочники РОВНО каршеринга — /car-refs, а не общий /refs бытового блока.
 * Причина не в объёме: в общем /refs лежат месяцы, категории (=ставки) и поимённый
 * список людей раздела «Овертаймы». Пока весь p73x закрыт на одну учётку, разницы нет;
 * как только каршеринг откроют шире, этот экран потянет справочники — и не должен
 * утащить с собой то, о чём решения не было. Отбор сделан в ядре, а не здесь:
 * см. p73x_api.справочники_каршеринга.
 */
export function useСправочникиКарш() {
  return useQuery<СправочникиКарш>({
    queryKey: ['carsharing', 'car-refs'],
    queryFn: () => запрос<СправочникиКарш>('/car-refs'),
    ...ОБЩЕЕ,
  })
}

export function useПарк(ф: Record<string, unknown>) {
  return useQuery<Список<МашинаСтрока> & { totals: ПаркИтоги; window_days: number }>({
    queryKey: ['carsharing', 'cars', ф],
    queryFn: () => запрос('/cars' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useПоездкиКарш(ф: Record<string, unknown>) {
  return useQuery<Список<ПоездкаСтрока> & { totals: ПоездкиИтоги }>({
    queryKey: ['carsharing', 'car-trips', ф],
    queryFn: () => запрос('/car-trips' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/**
 * Заправки личных авто — это компенсация расходов сотруднику, то есть деньги человека.
 * `all=1` ставится ЯВНО и всегда: ядро без адресата отвечает отказом, а не пустым
 * списком, и подписывает ответ restricted='owner'. Экран обязан эту подпись показать.
 */
export function useЗаправки(ф: Record<string, unknown>) {
  return useQuery<Список<ЗаправкаСтрока> & { totals: ЗаправкиИтоги }>({
    queryKey: ['carsharing', 'refuels', ф],
    queryFn: () => запрос('/refuels' + строкаЗапроса({ ...ф, all: true })),
    ...ОБЩЕЕ,
  })
}

/** Оговорки раздела, которые экран обязан произнести. Тексты — ядра, не наши. */
export const ОГОВОРКИ_КАРШЕРИНГА = [
  'car_plate_hidden',
  'car_dest_hidden',
  'car_docs_empty',
  'car_km_hand',
  'car_trips_imported',
  'refuel_money_thin',
  'refuel_balance_absent',
  'car_trips_lag',
]

/** Как называется сущность зеркала в подписи «обновлено N мин назад». */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'p73x:cars': 'парк',
  'p73x:car_trips': 'поездки',
  'p73x:car_refuels': 'заправки',
}

/** Самая отставшая из сущностей каршеринга. Свежесть показываем по ней, а не по средней. */
export function самаяСтарая(список?: Свежесть[]): Свежесть | undefined {
  const свои = (список ?? []).filter((с) => с.entity in ИМЯ_СУЩНОСТИ)
  if (!свои.length) return undefined
  return свои.reduce((а, б) => ((б.age_min ?? 0) > (а.age_min ?? 0) ? б : а))
}
